import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Route, Routes, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from './components/Layout';
import FilePage from './FilePage';
import API_URL from './api.config';
import Analytics from './components/Analytics';

function Home({trackEvent}) {
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');
  const [fileKey, setFileKey] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const navigate = useNavigate();
  const [isSampleLoading, setIsSampleLoading] = useState(false);


  const handleSampleFileUpload = async () => {
    trackEvent('FilePage', 'handleSampleFileUpload', fileKey);
    setIsSampleLoading(true);
    try {
      const response = await axios.get(`${API_URL}/sample-key`);
      setFileKey(response.data.key);
      setError('');
    } catch (error) {
      console.error('Error fetching sample file:', error);
      setError('Error fetching sample file');
    } finally {
      setIsSampleLoading(false);
    }
  };


  const handleUpload = useCallback(async (selectedFile) => {
    if (!selectedFile) return;

    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const response = await axios.post(API_URL + '/upload', formData);
      setFileKey(response.data.key);
      setError('');
    } catch (error) {
      console.error('Error uploading file:', error);
      setError(error.response?.data?.error || 'Error uploading file');
    } finally {
      setIsUploading(false);
    }
  }, []);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    handleFileValidationAndUpload(selectedFile);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const selectedFile = event.dataTransfer.files[0];
    handleFileValidationAndUpload(selectedFile);
  };

  const handleFileValidationAndUpload = (selectedFile) => {
    if (selectedFile) {
      if (selectedFile.type !== 'text/csv') {
        setError('Only CSV files are allowed');
        setFile(null);
      } else if (selectedFile.size > 5 * 1024 * 1024) {
        setError('File size exceeds the 5 MB limit');
        setFile(null);
      } else {
        setFile(selectedFile);
        setError('');
        handleUpload(selectedFile);
      }
    }
  };
  const handleDragOver = (event) => {
    event.preventDefault();
  };

  useEffect(() => {
    if (fileKey) {
      navigate(`/file/${fileKey}`);
    }
  }, [fileKey, navigate]);

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-background text-center py-4">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-6xl font-extralight mb-4 text-text">Upload, Share, Analyze with AI</h1>
          <p className="text-lg font-light text-text">
            Upload CSV files, get a link to share.<br />
            <span className="text-secondary">Analyze with <b className="text-lg font-medium text-text">Natural language</b> or <b className="text-lg font-medium text-text">SQL</b> running in your browser - <b className="text-lg font-medium text-text">No compute cost</b></span>
          </p>
        </div>
      </div>
      <div className="max-w-md bg-card rounded-lg p-8 text-center mx-auto mt-1">
        <div className="mb-2">
          <label
            htmlFor="file-upload"
            className="cursor-pointer block"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
          >
            <div className="border-2 border-dashed border-secondary rounded-lg p-6 hover:border-primary transition duration-300">
              <div className="text-4xl text-primary mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
              </div>
              <p className="text-sm font-md text-text">Drag & drop or Browse</p>
              <p className="text-xs text-secondary mt-1">Supported format: CSV</p>
              <p className="text-xs text-secondary mt-1">Max size: 5 MB</p>
            </div>
          </label>
          <input
            id="file-upload"
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            className="hidden"
          />
        </div>
        <div className="mt-4">
          <p className="text-sm text-text mb-2">Don't have a CSV file handy?</p>
          <button
            onClick={handleSampleFileUpload}
            disabled={isSampleLoading || isUploading}
            className={`w-full py-2 px-4 rounded-md text-white font-light transition duration-150 text-sm ${
              isSampleLoading || isUploading ? 'bg-secondary cursor-not-allowed' : 'bg-primary hover:bg-blue-600'
            }`}
          >
            {isSampleLoading ? 'Loading Sample...' : 'Try with Sample File'}
          </button>
        </div>
        {(isUploading || isSampleLoading) && <p className="text-primary text-sm mt-2">Processing...</p>}
        {error && <p className="text-red-600 mt-2 text-sm">{error}</p>}
      </div>
    </div>
  );
}

function App() {
  return (
    <Router>
    <Analytics>
    {({ trackEvent }) => (
      <Layout>
        <Routes>
          <Route path="/" element={<Home trackEvent={trackEvent} />} />
          <Route path="/file/:fileKey" element={<FilePage trackEvent={trackEvent} />} />
        </Routes>
      </Layout>
      )}
      </Analytics>
    </Router>
  );
}

export default App;